import { Exercise } from "./../../../models/Exercise";
import { addDays, format, startOfWeek } from "date-fns";
import { useEffect, useState } from "react";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import { GET_SCHEDULE_EXERCISES_V2 } from "../../../api/apiRoutes";
import { previousMonday } from "date-fns";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import { ExerciseScheduleList } from "../../../models/ExerciseScheduleList";
import { weekDays } from "../../../models/Data/Weekdays";
import { TicketAccess, setTicketAccessFromExercise } from "../../../models/TicketAccess";

export class Hours {
  constructor(data: any) {
    this.time = data.time;
    this.days = data.days;
  }
  time: string;
  days: Days[];
}

export class Days {
  constructor(data: any) {
    this.day = data.day;
    this.exercise = data.exercise;
  }
  day: string;
  exercise: Exercise[];
}

export default function useSchedule() {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const [exercisesList, setExercisesList] = useState<ExerciseScheduleList>({} as ExerciseScheduleList);
  const [dateFromState, setDateFrom] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [dateToState, setDateTo] = useState<Date>(new Date(addDays(new Date(dateFromState), 6)));
  const [dayFromStateMobile, setDayFromMobile] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [dayToStateMobile, setDayToMobile] = useState<Date>(new Date(addDays(new Date(dateFromState), 6)));
  const [exerciseGroupIds, setExerciseGroupIds] = useState<string>(``);
  const today = new Date();
  const userDataId = useSelector((state: State) => state.auth.userData.objectId);
  const [loading, setLoading] = useState(false);
  const [ticketAccess, setTicketAccess] = useState<TicketAccess>({} as TicketAccess);

  const getTicketAccess = (exercise: Exercise) => {
    let ticketAccessTransform = setTicketAccessFromExercise(exercise.objectId, exercise.name, exercise.startTime);
    setTicketAccess(ticketAccessTransform);
  };

  const resetSortByExerciseGroup = () => {
    setExerciseGroupIds(``);
    getScheduleActivity(format(dateFromState, "yyyy-MM-dd"), format(dateToState, "yyyy-MM-dd"), "");
  };

  const sortByExerciseGroup = (objectId: number) => {
    const Ids = `0,${objectId}`;
    setExerciseGroupIds(Ids);
  };

  const nextWeek = () => {
    const dateFrom = new Date(addDays(new Date(dateFromState), 7));
    const dateTo = new Date(addDays(new Date(dateToState), 7));
    setDateFrom(dateFrom);
    setDateTo(dateTo);
    getScheduleActivity(format(dateFrom, "yyyy-MM-dd"), format(dateTo, "yyyy-MM-dd"), exerciseGroupIds);
  };

  const previousWeek = () => {
    const dateFrom = new Date(addDays(new Date(dateFromState), -7));
    const dateTo = new Date(addDays(new Date(dateToState), -7));
    setDateFrom(dateFrom);
    setDateTo(dateTo);
    getScheduleActivity(format(dateFrom, "yyyy-MM-dd"), format(dateTo, "yyyy-MM-dd"), exerciseGroupIds);
  };

  const getScheduleActivity = async (
    dateFrom: string,
    dateTo: string,
    exercisesGroupId: string,
    disableLoading?: boolean
  ) => {
    setLoading(disableLoading ? false : true);
    const { result, isError } = await get(
      GET_SCHEDULE_EXERCISES_V2(facilityIds, dateFrom, dateTo, exercisesGroupId, userDataId)
    );
    if (!isError) {
      const convertedResult: ExerciseScheduleList = {
        Poniedziałek: [],
        Wtorek: [],
        Środa: [],
        Czwartek: [],
        Piątek: [],
        Sobota: [],
        Niedziela: [],
      };
      for (const day of weekDays) {
        convertedResult[day as keyof ExerciseScheduleList] = result[day] || [];
      }
      setExercisesList(convertedResult);
    }
    setLoading(false);
  };

  const refreshExercisesList = () => {
    getScheduleActivity(format(dateFromState, "yyyy-MM-dd"), format(dateToState, "yyyy-MM-dd"), exerciseGroupIds, true);
  };

  useEffect(() => {
    if (facilityIds && exerciseGroupIds) {
      getScheduleActivity(format(dateFromState, "yyyy-MM-dd"), format(dateToState, "yyyy-MM-dd"), exerciseGroupIds);
    }
    //eslint-disable-next-line
  }, [facilityIds, exerciseGroupIds]);

  const nextDay = () => {
    const dateFrom = new Date(addDays(new Date(dayFromStateMobile), 1));
    const dateTo = new Date(addDays(new Date(dayToStateMobile), 1));
    setDayFromMobile(dateFrom);
    setDayToMobile(dateTo);
    getScheduleActivity(format(dateFrom, "yyyy-MM-dd"), format(dateTo, "yyyy-MM-dd"), exerciseGroupIds);
  };

  const previousDay = () => {
    const dateFrom = new Date(addDays(new Date(dayFromStateMobile), -1));
    const dateTo = new Date(addDays(new Date(dayToStateMobile), -1));
    setDayFromMobile(dateFrom);
    setDayToMobile(dateTo);
    getScheduleActivity(format(dateFrom, "yyyy-MM-dd"), format(dateTo, "yyyy-MM-dd"), exerciseGroupIds);
  };

  const getFirstAllExercises = async () => {
    getScheduleActivity(format(dateFromState, "yyyy-MM-dd"), format(dateToState, "yyyy-MM-dd"), exerciseGroupIds);
  };

  return {
    getFirstAllExercises,
    previousDay,
    nextDay,
    nextWeek,
    previousWeek,
    sortByExerciseGroup,
    resetSortByExerciseGroup,
    dayFromStateMobile,
    dayToStateMobile,
    dateToState,
    dateFromState,
    today,
    exercisesList,
    loading,
    ticketAccess,
    getTicketAccess,
    refreshExercisesList,
  };
}
